import { Link, useLocation } from 'react-router-dom';
import { Phone, MapPin, Mail, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState } from 'react';
import { useClinicBranding, clinicLogo } from '../context/ClinicBrandingContext';

function telHref(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length ? `tel:+${digits}` : '#';
}

function shortLocationLine(info, fallbackCity) {
  if (info?.site_tagline_short && String(info.site_tagline_short).trim()) return info.site_tagline_short.trim();
  return fallbackCity;
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { info } = useClinicBranding();

  const brandName = info?.name || 'PodoclinicAM';
  const logo = clinicLogo(info);
  const phone = info?.phone || '+52 1 595 108 9300';
  const mail = info?.email || 'danielortegalozano@gmail.com';
  const addr = info?.address || 'Cd Sahagún, Hgo.';
  const topRight = shortLocationLine(info, addr.length > 48 ? `${addr.slice(0, 45)}…` : addr);

  const navItems = [
    { name: 'Inicio', path: '/' },
    { name: 'Servicios', path: '/productos' },
    { name: 'Reseñas', path: '/resenas' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white/95 border-b border-border/40 backdrop-blur-sm" data-testid="main-header">
      <div className="bg-primary text-primary-foreground py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between text-sm gap-2">
          <div className="flex items-center gap-4 flex-wrap">
            <a href={telHref(phone)} className="flex items-center gap-1 hover:underline">
              <Phone className="w-3 h-3" />
              <span>{phone}</span>
            </a>
            <a href={`mailto:${mail}`} className="hidden sm:flex items-center gap-1 hover:underline">
              <Mail className="w-3 h-3" />
              <span>{mail}</span>
            </a>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3 shrink-0" />
            <span className="hidden sm:inline max-w-xl truncate">{topRight}</span>
            <span className="sm:hidden max-w-[10rem] truncate">{shortLocationLine(info, 'Sahagún, Hgo.')}</span>
          </div>
        </div>
      </div>

      <nav className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img src={logo} alt={brandName} className="h-14 w-auto max-w-[220px] object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-primary'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/admin/login">
              <Button variant="ghost" size="sm" data-testid="admin-login-btn">
                Admin
              </Button>
            </Link>
            <Link to="/agendar">
              <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="book-appointment-btn">
                Agendar Cita
              </Button>
            </Link>
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-border pt-4 animate-fadeIn">
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`font-medium py-2 ${
                    isActive(item.path) ? 'text-primary' : 'text-muted-foreground'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="flex gap-2 mt-2">
                <Link to="/admin/login" className="flex-1">
                  <Button variant="outline" className="w-full" onClick={() => setMobileMenuOpen(false)}>
                    Admin
                  </Button>
                </Link>
                <Link to="/agendar" className="flex-1">
                  <Button className="w-full rounded-full" onClick={() => setMobileMenuOpen(false)}>
                    Agendar Cita
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export function Footer() {
  const { info } = useClinicBranding();
  const brandName = info?.name || 'PodoclinicAM';
  const logo = clinicLogo(info);
  const phone = info?.phone || '+52 1 595 108 9300';
  const mail = info?.email || 'danielortegalozano@gmail.com';
  const address = info?.address || 'Imán, Miguel Hidalgo, 43998 Cd Sahagún, Hgo.';
  const blurb =
    info?.footer_blurb ||
    'Especialistas en el cuidado de tus pies. Brindamos atención profesional y personalizada para tu bienestar.';
  const hw =
    info?.footer_hours_weekdays || 'Lunes a Viernes: 9:00 AM - 5:00 PM';
  const hl = info?.footer_hours_lunch || 'Hora de comida: 2:00 PM - 3:00 PM';
  const hwe = info?.footer_hours_weekend || 'Sábado y Domingo: Cerrado';
  const copy = info?.copyright_name || brandName;

  return (
    <footer className="bg-primary text-primary-foreground" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <img src={logo} alt={brandName} className="h-16 w-auto max-w-[200px] object-contain mb-4 brightness-0 invert" />
            <p className="text-primary-foreground/80 text-sm leading-relaxed">{blurb}</p>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Horarios</h4>
            <div className="space-y-2 text-sm text-primary-foreground/80">
              <p>{hw}</p>
              <p>{hl}</p>
              <p>{hwe}</p>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">Contacto</h4>
            <div className="space-y-3 text-sm">
              <a
                href={telHref(phone)}
                className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
              >
                <Phone className="w-4 h-4" />
                {phone}
              </a>
              <a
                href={`mailto:${mail}`}
                className="flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors"
              >
                <Mail className="w-4 h-4" />
                {mail}
              </a>
              <p className="flex items-start gap-2 text-primary-foreground/80">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {address}
              </p>
            </div>
          </div>
        </div>

        <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-sm text-primary-foreground/60">
          <p>
            © {new Date().getFullYear()} {copy}. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
}
